import { toTaskSearchRequestDto } from '../model/mappers/search-request.dto';
import { toTaskModel } from '../model/mappers/task.model';

import type { TaskSearchRequestDto } from '../model/types/search-request.dto';
import type { TaskSearchRequestModel } from '../model/types/search-request.model';
import type { Task } from '../model/types/task.dto';
import type { TaskModel } from '../model/types/task.model';
import type { MessageCreateModel } from '@/features/Shared';
import type { SearchResponseDto } from '@/features/Shared/model/types/search-response.dto';
import type { SearchResponseModel } from '@/features/Shared/model/types/search-response.model';

import { apiSlice } from '@/app/api/api';
import { toMessageCreateDto } from '@/features/Shared/model/mappers/message-create.dto';

export const taskApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		tasks: builder.infiniteQuery<
			SearchResponseModel<TaskModel>,
			TaskSearchRequestModel,
			string | undefined
		>({
			infiniteQueryOptions: {
				initialPageParam: undefined,
				getNextPageParam: (lastPage: SearchResponseModel<TaskModel>) => {
					return lastPage.moreResults ? (lastPage.continuation ?? undefined) : undefined;
				},
			},

			query: ({ queryArg, pageParam }) => ({
				url: '/tasks/search',
				method: 'POST',
				body: {
					...toTaskSearchRequestDto(queryArg),
					continuation_token: pageParam ?? undefined,
				} satisfies TaskSearchRequestDto,
			}),
			transformResponse: (response: SearchResponseDto<Task>) => {
				return {
					items: (response.items ?? []).map(toTaskModel),
					continuation: response.meta.continuation,
					moreResults: response.meta.more_results,
				};
			},
		}),
		createTask: builder.mutation<void, MessageCreateModel>({
			query: (item) => {
				return {
					url: `/tasks`,
					method: 'POST',
					body: toMessageCreateDto(item),
				};
			},
			invalidatesTags: () => [{ type: 'TASKS', id: 'PARTIAL-LIST' }],
		}),
		deleteTask: builder.mutation<void, { id: string; organizationId: string }>({
			query: ({ id, organizationId }) => {
				return {
					url: `/organizations/${organizationId}/tasks/${id}`,
					method: 'DELETE',
				};
			},
			invalidatesTags: () => [{ type: 'TASKS', id: 'PARTIAL-LIST' }],
		}),
	}),

	overrideExisting: false,
});

export const { useTasksInfiniteQuery, useCreateTaskMutation, useDeleteTaskMutation } = taskApiSlice;
